import React, { useEffect, useState } from 'react'
import T from 'prop-types'
import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'

import InlineTextField from 'components/atoms/InlineTextField'
import CarCardAdditionalDetails from 'components/atoms/CarCardAdditionalDetails'
import CarCardAdditionalDetailsItem from 'components/atoms/CarCardAdditionalDetailsItem'

const getPriceValueClickHandler = (
  readOnly,
  toggleEditGroupPrice,
  plate,
  group
) => {
  if (readOnly) {
    return undefined
  }

  return () => toggleEditGroupPrice(plate, group)
}

export default function CarPrices({
  plate,
  prices = [],
  vertical,
  handleChangePrice,
  toggleEditGroupPrice = () => {},
  handleChangeGroupPrice = () => {},
  edit,
  readOnly,
}) {
  const { t } = useTranslation()

  const numberFormatter = new Intl.NumberFormat('it-IT')

  const [localValue, setLocalValue] = useState({})

  useEffect(() => {
    setLocalValue(
      prices.reduce(
        (previous, price) => ({ ...previous, [price.group]: price.price }),
        {}
      )
    )
  }, [prices])

  const handleLocalChangeGroupPrice = (group, value) => {
    setLocalValue((v) => ({ ...v, [group]: value.replace(/[^0-9]/g, '') }))
  }

  const handleBlurGroupPrice = (group, value) => {
    toggleEditGroupPrice(plate, group)
    handleChangeGroupPrice(plate, group, Number(value))
  }

  return (
    <CarCardAdditionalDetails vertical={vertical} edit={edit}>
      {prices
        .filter((price) => price.required)
        .map((price) => {
          return (
            <CarCardAdditionalDetailsItem
              id={`${plate}-${price.group}`}
              vertical={vertical}
              key={price.group}
              title={t(
                `sessions.${price.group === 0 ? 'reservePrice' : 'group'}`,
                {
                  attribute: price.group,
                }
              )}
              titleProps={{
                variant: vertical && price.group === 0 ? 'body2' : 'caption',
                color: price.edit ? 'primary' : 'textSecondary',
                sx: { fontWeight: price.edited ? 'bold' : undefined },
              }}
              value={numberFormatter.format(localValue[price.group])}
              valueProps={{
                variant: vertical && price.group === 0 ? 'body2' : 'caption',

                onClick: getPriceValueClickHandler(
                  readOnly,
                  toggleEditGroupPrice,
                  plate,
                  price.group
                ),
              }}
              valueNode={
                !price.edit || readOnly ? undefined : (
                  <InlineTextField
                    id={`${plate}-${price.group}`}
                    autoFocus
                    value={localValue[price.group]}
                    border
                    fontSize={
                      vertical && price.group === 0
                        ? 'body2.fontSize'
                        : 'caption.fontSize'
                    }
                    inputProps={{
                      sx: {
                        textAlign: vertical ? 'right' : undefined,
                      },
                    }}
                    onChange={(event) =>
                      handleLocalChangeGroupPrice(
                        price.group,
                        event.target.value
                      )
                    }
                    onBlur={(event) =>
                      handleBlurGroupPrice(price.group, event.target.value)
                    }
                    sx={{ width: '6ch' }}
                  />
                )
              }
            />
          )
        })}
      {handleChangePrice && (
        <Button
          data-testid={`button-change-${plate}`}
          variant="contained"
          disabled={readOnly}
          onClick={handleChangePrice}
          fullWidth
        >
          {t(`sessions.${!edit ? 'change' : 'save'}Price`)}
        </Button>
      )}
    </CarCardAdditionalDetails>
  )
}

CarPrices.propTypes = {
  plate: T.string.isRequired,
  prices: T.array,
  vertical: T.bool,
  handleChangePrice: T.func,
  toggleEditGroupPrice: T.func,
  handleChangeGroupPrice: T.func,
  edit: T.bool,
  readOnly: T.bool,
}
